import React from 'react';
import parseISO from 'date-fns/parseISO';
import {FormattedMessage, FormattedTime} from 'react-intl';

import getPrimaryEventUrl from './getPrimaryEventUrl';
import getTicketBisEventUrl from './getTicketBisEventUrl';
import isPrimaryEvent, {EVENT_SOURCE_ID_PRIMARY} from './isPrimaryEvent';
import isTicketBisEvent, {EVENT_SOURCE_ID_TICKET_BIZ} from './isTicketBisEvent';

const dateRegEx = /[+-]\d{2}:?\d{2}$/i;

export const EVENT_TYPE_PARKING = 'parking';
export const HOME_TEAM = 'HOME_TEAM';
export const AWAY_TEAM = 'AWAY_TEAM';

/**
 * `TBD` Localized Message for rendered on the UI.
 * @returns TBDMessage [ReactElement] Returns a localized TBD Message Element to be rendered on the UI.
 *
 */
export const TBDMessage = <FormattedMessage id="EventItem.TimeTBD" defaultMessage="TBD" description="Event Time TBD" />;

/**
 * `Loading` Localized Message for rendered on the UI.
 * @returns loadingMessageText [ReactElement] Returns a localized `Loading` Message Element to be rendered on the UI.
 *
 */
export const loadingMessageText = <FormattedMessage id="EventList.loading" defaultMessage="Loading" />;

/**
 * `See More Events` Localized Message for rendered on the UI.
 * @returns seeMoreEventsText [ReactElement] Returns a localized `See More Events` Message Element to be rendered on the UI.
 *
 */
export const seeMoreEventsText = <FormattedMessage id="EventList.seeMore" defaultMessage="See more events" />;

/**
 * Method to Extract the Event Time from the Event Object of the catalog events API.
 * @param event [object] The Event Object from the catalog events api response.
 * @returns eventTime [ReactElement] Returns a react element for the formatted event time.
 *
 */
export const getEventTime = (event) => {
  if (event) {
    const {dateLocal = '', eventDateLocal, displayAttributes = {}} = event;
    const {hideEventTime} = displayAttributes;
    const eventDate = (eventDateLocal || dateLocal).replace(dateRegEx, '').replace('Z', '');
    const date = parseISO(eventDate);

    return hideEventTime || !eventDate ? TBDMessage : <FormattedTime value={date} />;
  }
};

/**
 * Method to Extract the Event Date from the Event Object of the catalog events API.
 * @param event [object] The Event Object from the catalog events api response.
 * @returns eventDate [Date] Returns a parsed and un-formatted Event date.
 *
 */
export const getEventDate = (event) => {
  if (event) {
    const {dateLocal = '', eventDateLocal, displayAttributes = {}} = event;
    const {hideEventDate} = displayAttributes;

    if (!hideEventDate) {
      const eventDate = (eventDateLocal || dateLocal).replace(dateRegEx, '').replace('Z', '');

      return parseISO(eventDate || '');
    }
  }

  return null;
};

/**
 * This helper function is to validate string for emptiness,
 * to avoid weak type comparison problems in JS
 * @param str {string} - string to be validated
 * @return {boolean}
 */
export const isNotEmptyString = (str) => !!str && typeof str === 'string' && !/^\s*$/.test(str);

/**
 * Method to Extract the Event Venue information from the Event Object of the catalog events API.
 * @param event [object] The Event Object from the catalog events api response.
 * @r
 *
 *  TODO: Location display should be location + country based.
 *  if searching from US, The Location of the Events in Paris should Show like
 *   1. Paris, France
 *   2. Paris, TN
 *   3. Paris, FL
 *
 *  if searching from Europe, The Location of the Events in Paris should Show like
 *   1. Paris, France
 *   2. Paris, TN, United States
 *   3. Paris, FL, United States
 *
 */
export const getVenueInfo = (event) => {
  let {venueName} = event;
  const {venue = {}} = event;

  const {webURI, name, uri, city, state, country} = venue;

  /**
   * Venue Name
   *
   */
  venueName = venueName || name;

  /**
   * Venue Location
   *
   */
  let venueLocation;

  if (isNotEmptyString(city)) {
    venueLocation = city;
  }
  if (isNotEmptyString(state)) {
    venueLocation = venueLocation ? `${venueLocation}, ${state}` : state;
  }
  if (isNotEmptyString(country)) {
    venueLocation = venueLocation ? `${venueLocation}, ${country}` : country;
  }

  return {
    venueName,
    venueLocation,
    venueCountry: country,
    venueWebURI: webURI || uri,
  };
};

/**
 * Method to return the `tickets left` info to be shown on the UI for the given event.
 * @param event [Object] The Event API Object
 * @param threshold [number] The minimum number of tickets at which the message should be shown.
 * @returns ticketsLeft [ReactElement] Returns a localized `ticketsLeft` Message Element to be rendered on the UI.
 *
 */
export const getTicketLeftInfo = (event, threshold = 0) => {
  const {ticketInfo = {}} = event;
  const {totalTickets} = ticketInfo;
  const showTicketsLeft = !isTicketBisEvent(event) && (totalTickets < threshold || totalTickets < 0);

  return showTicketsLeft ? (
    <FormattedMessage
      id="EventItem.ticketsLeft"
      defaultMessage="{totalTickets, plural, =0 {We have no tickets available. Come back later} one {# ticket left on our site} other {# tickets left on our site}}"
      values={{totalTickets: ticketInfo.totalTickets}}
    />
  ) : null;
};

const performerFilter = (primaryPerformerId, primary) => {
  return (element = {}) => {
    const curPerfId = parseInt(element.id);
    const priPerfId = parseInt(primaryPerformerId);

    return primary ? curPerfId === priPerfId : curPerfId !== priPerfId;
  };
};

/**
 * Method to get the Non Primary performer from the `PerformerCollection` of the event.
 * @param primaryPerformerId [number] The Primary Performer ID associated in the Page.
 * @param event [object={}] The Instance of the Event Object got from the Search Catalog Events API.
 * @param primary [boolean] Flag indicating if the Performer Object that has to be returned is primary or non-primary.
 *
 */
export const getEventPerformerObj = (primaryPerformerId, event = {}, primary) => {
  const {performersCollection = []} = event;

  return performersCollection.filter(performerFilter(primaryPerformerId, primary))[0];
};

export {
  getTicketBisEventUrl,
  isTicketBisEvent,
  EVENT_SOURCE_ID_TICKET_BIZ,
  getPrimaryEventUrl,
  isPrimaryEvent,
  EVENT_SOURCE_ID_PRIMARY,
};
